import { Button, Icon, NonIdealState, Spinner } from '@blueprintjs/core';
import deepEqual from 'deep-equal';
import React, { useEffect, useState } from 'react';

import { useAppDispatch, useAppSelector } from 'hooks';
import {
  addClimber,
  fetchClimbers,
  fetchClubs,
  updateClimber,
} from 'store/registration';
import ClimberRow from './ClimberRow';
import styles from './styles.module.scss';

type Sort = {
  column: keyof RegisteredClimber;
  ascending: boolean;
};

export default function ClimbersTable() {
  const climbers = useAppSelector((state) => state.registration.climbers) || [];
  const clubs = useAppSelector((state) => state.registration.clubs) || [];

  const [loading, setLoading] = useState(false);

  const [addingNewClimber, setAddingNewClimber] = useState(false);
  const [sort, setSort] = useState<Sort>({
    column: 'number',
    ascending: true,
  });

  const [modifiedClimber, setModifiedClimber] = useState<RegisteredClimber>();
  const [sortedClimbers, setSortedClimbers] = useState<RegisteredClimber[]>([]);

  const dispatch = useAppDispatch();

  async function reloadData() {
    setLoading(true);
    await Promise.all([dispatch(fetchClubs()), dispatch(fetchClimbers())]);
    setLoading(false);
  }

  useEffect(() => {
    reloadData();
  }, []);

  useEffect(() => {
    if (!climbers.length) {
      return;
    }

    setSortedClimbers(
      climbers.slice(0).sort((c1, c2) => {
        const val1 = sort.ascending ? c1[sort.column] : c2[sort.column];
        const val2 = sort.ascending ? c2[sort.column] : c1[sort.column];

        switch (typeof val1) {
          case 'number':
            return val1 - ((val2 as number) ?? 0);
          default:
            return String(val1).localeCompare(String(val2));
        }
      }),
    );
  }, [climbers, sort]);

  function handleClimberChange(climber: RegisteredClimber) {
    const originalClimber = climbers.find((c) => c.id === climber.id)!;

    setModifiedClimber(
      deepEqual(climber, originalClimber) ? undefined : climber,
    );
  }

  async function onClimberEdit(climber: RegisteredClimber) {
    await dispatch(updateClimber(climber));
    await reloadData();
    setModifiedClimber(undefined);
  }

  const usedNumbers = climbers.map((c) => c.number);

  if (loading && !climbers.length) {
    return <NonIdealState icon={<Spinner />} title="loading" />;
  }

  return (
    <div className={styles.climbersTable}>
      <HeaderRow sort={sort} setSort={setSort} />

      {sortedClimbers.map((climber) => {
        const climberChanged = modifiedClimber?.id === climber.id;

        return (
          <ClimberRow
            key={climber.id}
            climberData={climberChanged ? modifiedClimber : climber}
            originalNumber={climber.number}
            climberChanged={climberChanged}
            usedNumbers={usedNumbers}
            clubs={clubs}
            onClimberChange={handleClimberChange}
            onSave={onClimberEdit}
            onDiscard={() => setModifiedClimber(undefined)}
            editDisabled={
              addingNewClimber || (!climberChanged && !!modifiedClimber)
            }
          />
        );
      })}

      <NewClimber
        disabled={!!modifiedClimber}
        addingNewClimber={addingNewClimber}
        setAddingNewClimber={setAddingNewClimber}
        clubs={clubs}
        reloadData={reloadData}
        usedNumbers={usedNumbers}
      />
    </div>
  );
}

function HeaderRow({
  sort,
  setSort,
}: {
  sort: Sort;
  setSort: (sort: Sort) => void;
}) {
  const columns: { id: keyof RegisteredClimber; text: string }[] = [
    { id: 'number', text: 'Nr.' },
    { id: 'name', text: 'Name' },
    { id: 'category', text: 'Category' },
    { id: 'club', text: 'Club' },
    { id: 'country', text: 'Country' },
    { id: 'paid', text: 'Paid' },
    { id: 'notes', text: 'Notes' },
  ];

  return (
    <>
      {columns.map((col) => (
        <div key={col.id} className={styles.headerRow}>
          <div
            className={styles.sortingHeader}
            onClick={() =>
              setSort({
                column: col.id,
                ascending: sort.column === col.id ? !sort.ascending : true,
              })
            }
          >
            <span>{col.text}</span>
            {sort.column === col.id && (
              <Icon
                color="#555"
                icon={sort.ascending ? 'caret-up' : 'caret-down'}
              />
            )}
          </div>
        </div>
      ))}
      <div />
    </>
  );
}

function NewClimber({
  clubs,
  addingNewClimber,
  setAddingNewClimber,
  reloadData,
  usedNumbers,
  disabled,
}: {
  clubs: string[];
  addingNewClimber: boolean;
  setAddingNewClimber: (val: boolean) => void;
  reloadData: () => Promise<void>;
  usedNumbers: number[];
  disabled: boolean;
}) {
  const dispatch = useAppDispatch();

  async function onClimberAdd() {
    const climberAdded = await dispatch(addClimber(climberData));
    if (!climberAdded) {
      return;
    }
    await reloadData();
    setAddingNewClimber(false);
    setClimberData(getEmptyClimberData());
  }

  const [climberData, setClimberData] = useState<RegisteredClimber>(
    getEmptyClimberData(),
  );

  if (!addingNewClimber) {
    return (
      <>
        <div />
        <Button
          disabled={disabled}
          style={{ marginTop: '16px' }}
          onClick={() => setAddingNewClimber(true)}
          icon="plus"
          intent="success"
          text={'New climber'}
        />
      </>
    );
  }

  return (
    <ClimberRow
      climberData={climberData}
      editDisabled={false}
      onClimberChange={setClimberData}
      climberChanged={true}
      usedNumbers={usedNumbers}
      originalNumber={0}
      clubs={clubs}
      onSave={onClimberAdd}
      onDiscard={() => setAddingNewClimber(false)}
    />
  );
}

function getEmptyClimberData(): RegisteredClimber {
  return {
    id: '',
    number: 0,
    name: '',
    category: '',
    country: '',
    paid: 0,
    notes: '',
  };
}
